import React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { ErrorMessage } from "formik";
import { TextInputComponentProps } from "./text-input-component.props";
import "./text-input-component.scss";
import InputAdornment from "@mui/material/InputAdornment";
import lockIcon from "../../../assets/icons/lock-icon.svg";

const TextInputComponent: React.FC<TextInputComponentProps> = ({
  fieldName = "",
  label,
  placeholder = "",
  optional = false,
  disabled,
  value,
  type,
  onChange,
  minlength,
  maxlength,
  isRequired = false,
  isName = false,
  isAlphaNumeric = false,
  allowSpecialCharacters = false,
  handleBlur,
  isNumber = false,
  blockInvalidChars,
  isStreet,
  variant,
  multiline,
  rows,
  isLocked = false,
  isShowError = false,
  autoComplete,
  renderErrorMessage = true,
  readOnly = false,
  isDecimal = false,
  beforeDecimalMaxLength,
  afterDecimalMaxLength,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // block 'e', 'E', '+', '-', etc.
    if (["e", "E", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };
  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const { value, selectionStart } = input;

    const restoreCursor = (newValue: string, cursorPos: number) => {
      input.value = newValue;
      const pos = Math.min(cursorPos, newValue.length);
      input.setSelectionRange(pos, pos);
    };
    if (type === "email") {
      const sanitizedValue = value.replace(/[^A-Za-z0-9._%+-@]/g, "");
      const [localPart, domainPart] = sanitizedValue.split("@");

      if (domainPart !== undefined) {
        if (domainPart.includes("..")) {
          event.target.value = `${localPart}@${domainPart.replace(/\.+/g, ".")}`;
        } else if (domainPart.split(".").length > 2) {
          const parts = domainPart.split(".");
          const extension = parts.pop();
          const domain = parts.join(".");
          event.target.value = `${localPart}@${domain}.${extension?.slice(0, 6)}`;
        }
      }
    } else if (isName) {
      event.target.value = value.replace(/[^A-Za-z -]/g, "");
    } else if (isAlphaNumeric) {
      event.target.value = value.replace(/[^A-Za-z0-9 ]/g, "");
    } else if (allowSpecialCharacters) {
      event.target.value = value.replace(/[^A-Za-z0-9\s&.,'/#()-]/g, "");
    } else if (isStreet) {
      event.target.value = value.replace(/[^A-Za-z0-9\s&.,'/#()-]/g, "");
    } else if (isNumber) {
      let sanitized = value.replace(/\D/g, "");
      const maxLen = Number(maxlength) || sanitized.length;
      sanitized = sanitized.slice(0, maxLen);

      const newCursorPos = selectionStart ?? sanitized.length;
      restoreCursor(sanitized, newCursorPos);
    } else if (isDecimal) {
      let sanitized = value.replace(/[^0-9.]/g, "");

      // Only allow one dot
      const dotIndex = sanitized.indexOf(".");
      if (dotIndex !== -1) {
        sanitized =
          sanitized.slice(0, dotIndex + 1) +
          sanitized.slice(dotIndex + 1).replace(/\./g, "");
      }

      let [beforeDecimal, afterDecimal = ""] = sanitized.split(".");

      const MAX_BEFORE = beforeDecimalMaxLength ?? 7;
      const MAX_AFTER = afterDecimalMaxLength ?? 2;

      beforeDecimal = beforeDecimal.slice(0, MAX_BEFORE);
      afterDecimal = afterDecimal.slice(0, MAX_AFTER);

      const hasDot = sanitized.includes(".");
      const newValue = hasDot
        ? `${beforeDecimal}.${afterDecimal}`
        : beforeDecimal;

      const newCursorPos = selectionStart ?? newValue.length;
      restoreCursor(newValue, newCursorPos);
    }
  };

  return (
    <Box>
      <div
        className={`form-text-input-component ${isRequired ? "required" : ""}`}
      >
        {label && (
          <InputLabel required={optional} htmlFor={fieldName}>
            {label}
          </InputLabel>
        )}
        <FormControl>
          <TextField
            variant="outlined"
            name={fieldName}
            placeholder={placeholder}
            required={optional}
            disabled={disabled}
            id={fieldName}
            value={value}
            multiline={multiline}
            rows={rows}
            onChange={onChange}
            onBlur={handleBlur}
            autoComplete={autoComplete}
            onInput={handleInput}
            type={type}
            slotProps={{
              input: {
                id: fieldName,
                name: fieldName,
                required: optional,
              },
            }}
            inputProps={{
              minLength: minlength,
              maxLength: maxlength,
              readOnly: readOnly,
            }}
            sx={{
              "& .MuiInputBase-input::placeholder": {
                color: isShowError ? "#dc3545" : "#aaa",
                opacity: 1,
              },
            }}
            onKeyDown={blockInvalidChars ? handleKeyDown : undefined}
            InputProps={{
              endAdornment: isLocked ? (
                <InputAdornment position="end">
                  <img
                    src={lockIcon}
                    alt="lock icon"
                    style={{ height: "20px", width: "20px" }}
                  />
                </InputAdornment>
              ) : null,
              readOnly: isLocked, // Optional: prevent typing if locked
            }}
          />
          {/* {isLocked && (
            <Box>
              <img src={lockIcon} alt="lock icon" style={{ width: 20, height: 20 }} />
            </Box>
          )} */}
        </FormControl>
        {renderErrorMessage && (
          <ErrorMessage
            name={fieldName || ""}
            component="div"
            className="error-message"
          />
        )}
      </div>
    </Box>
  );
};

export default TextInputComponent;
